/**
 * Medium bouncing cursor + cosmetics on Pause / Game Over screens.
 * Constant speed, edge bounce, animated cosmetic frames via equipped layers.
 */
import { drawEquippedCosmeticOverlays, equippedHidesBaseCursor } from "./cursor-cosmetics";

const CANVAS_CLASS = "idleShowcaseCursorCanvas";

type ShowcaseState = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  raf: number;
  lastTs: number;
  getCursorColor: () => string;
};

let state: ShowcaseState | null = null;

function mediumRadius(): number {
  const m = Math.min(window.innerWidth, window.innerHeight);
  return Math.max(34, Math.min(52, m * 0.058));
}

function constantSpeed(): number {
  const m = Math.min(window.innerWidth, window.innerHeight);
  return Math.max(70, Math.min(110, m * 0.09));
}

function spawnInside(r: number, w: number, h: number): { x: number; y: number; vx: number; vy: number } {
  const pad = r + 8;
  const bw = Math.max(pad * 2, w - pad * 2);
  const bh = Math.max(pad * 2, h - pad * 2);
  const x = pad + Math.random() * bw;
  const y = pad + Math.random() * bh;
  const angle = Math.random() * Math.PI * 2;
  const speed = constantSpeed();
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
  };
}

function resizeCanvas(canvas: HTMLCanvasElement) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = window.innerWidth;
  const h = window.innerHeight;
  canvas.width = Math.max(1, Math.floor(w * dpr));
  canvas.height = Math.max(1, Math.floor(h * dpr));
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  const ctx = canvas.getContext("2d");
  if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}

function bounce(s: ShowcaseState, w: number, h: number) {
  const { r } = s;
  // Keep speed magnitude unchanged — only flip axis on edge hit.
  if (s.x < r) {
    s.x = r;
    s.vx = Math.abs(s.vx);
  } else if (s.x > w - r) {
    s.x = w - r;
    s.vx = -Math.abs(s.vx);
  }
  if (s.y < r) {
    s.y = r;
    s.vy = Math.abs(s.vy);
  } else if (s.y > h - r) {
    s.y = h - r;
    s.vy = -Math.abs(s.vy);
  }
}

function drawFrame(s: ShowcaseState) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const { ctx, r } = s;
  ctx.clearRect(0, 0, w, h);
  const color = s.getCursorColor() || "#ffffff";
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  if (!equippedHidesBaseCursor()) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  drawEquippedCosmeticOverlays(ctx, s.x, s.y, r, {
    liveCursorColor: color,
  });
  ctx.restore();
}

function tick(ts: number) {
  if (!state) return;
  const s = state;
  if (!s.lastTs) s.lastTs = ts;
  let dt = (ts - s.lastTs) / 1000;
  s.lastTs = ts;
  // Cap dt so a long tab-hide doesn't teleport; speed stays constant.
  if (dt > 0.05) dt = 0.05;
  if (dt < 0) dt = 0;

  const w = window.innerWidth;
  const h = window.innerHeight;
  s.x += s.vx * dt;
  s.y += s.vy * dt;
  bounce(s, w, h);
  drawFrame(s);
  s.raf = requestAnimationFrame(tick);
}

function onResize() {
  if (!state) return;
  resizeCanvas(state.canvas);
  state.r = mediumRadius();
  const w = window.innerWidth;
  const h = window.innerHeight;
  state.x = Math.max(state.r, Math.min(w - state.r, state.x));
  state.y = Math.max(state.r, Math.min(h - state.r, state.y));
  // Re-normalize velocity to constant speed after resize (no acceleration).
  const speed = constantSpeed();
  const mag = Math.hypot(state.vx, state.vy) || 1;
  state.vx = (state.vx / mag) * speed;
  state.vy = (state.vy / mag) * speed;
  drawFrame(state);
}

/** Start (or restart) the bouncing showcase cursor overlay. */
export function startIdleShowcaseCursor(getCursorColor: () => string) {
  stopIdleShowcaseCursor();
  const canvas = document.createElement("canvas");
  canvas.className = CANVAS_CLASS;
  canvas.setAttribute("aria-hidden", "true");
  document.body.appendChild(canvas);
  resizeCanvas(canvas);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    canvas.remove();
    return;
  }
  const r = mediumRadius();
  const spawn = spawnInside(r, window.innerWidth, window.innerHeight);
  state = {
    canvas,
    ctx,
    x: spawn.x,
    y: spawn.y,
    vx: spawn.vx,
    vy: spawn.vy,
    r,
    raf: 0,
    lastTs: 0,
    getCursorColor,
  };
  window.addEventListener("resize", onResize);
  state.raf = requestAnimationFrame(tick);
}

export function stopIdleShowcaseCursor() {
  if (!state) return;
  cancelAnimationFrame(state.raf);
  window.removeEventListener("resize", onResize);
  state.canvas.remove();
  state = null;
}

export function isIdleShowcaseCursorActive(): boolean {
  return !!state;
}
